import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useState } from 'react';
import { Button, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import LeadCloseDeleteConfirm from 'src/components/LeadClose/LeadCloseDeleteConfirm.component';
import LeadCloseRecord from 'src/components/LeadClose/LeadCloseRecord.component';
import { toCloseLeadRef } from 'src/components/LeadClose/LeadClose.functions';
import type { tCloseResult } from 'src/components/LeadClose/LeadClose.types';
import { useLeadCloseFeed } from 'src/components/LeadClose/useLeadCloseFeed.hook';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import Record from './components/Record/Record.component.tsx';
import useRecords from './Records.hook.ts';
import './Records.styles.scss';

function instant(stamp: string): number {
  return Date.parse(
    stamp.includes('T') ? stamp : `${stamp.replace(' ', 'T')}Z`,
  );
}

export default function Records() {
  const { historyData, isLoading, getHistoryData } = useRecords();
  const { t } = useTranslation('chat/chat');
  const leadClose = useLeadClose();
  const { selectedLeadForChatModal } = useModalsContext();
  // LEA-50 (U15): the lead's result rows join Historial, read only while it is open.
  const feed = useLeadCloseFeed(
    leadClose.enabled ? (selectedLeadForChatModal?.id ?? null) : null,
  );
  const [deleting, setDeleting] = useState<tCloseResult | null>(null);
  // After a refused deletion Historial reads again: the confirmation takes the
  // current revision of the same result, so the next try can go through.
  useEffect(() => {
    if (!deleting) return;
    const fresh = feed.rows.find((row) => row.result?.uuid === deleting.uuid)?.result;
    if (fresh && !fresh.live) setDeleting(null);
    else if (fresh && fresh.revision !== deleting.revision) setDeleting(fresh);
  }, [feed.rows, deleting]);

  const legacy = (historyData ?? []).map((record, index) => ({
    at: Date.parse(record.createdAt),
    key: `record-${index}`,
    node: <Record key={`record-${index}`} {...record} />,
  }));

  let items: { at: number; key: string; node: ReactNode }[] = legacy;
  if (leadClose.enabled && selectedLeadForChatModal) {
    const lead = toCloseLeadRef(selectedLeadForChatModal);
    const seen = new Set<string>();
    const results = feed.rows
      .slice()
      .sort((a, b) => instant(b.created_at) - instant(a.created_at))
      .map((row) => {
        // Pencil and trash go on the newest row of each result.
        // The old-amount row shares the instant and comes first: it never takes the slot.
        const uuid = row.field === 'close_prior_amount' ? '' : (row.result?.uuid ?? '');
        const actionable = Boolean(uuid) && !seen.has(uuid);
        if (uuid) seen.add(uuid);
        return {
          at: instant(row.created_at),
          key: `close-${row.id}`,
          node: (
            <LeadCloseRecord
              key={`close-${row.id}`}
              row={row}
              actionable={actionable}
              onEdit={(result) =>
                leadClose.openForm({
                  lead,
                  type: result.type,
                  source: 'lead_panel',
                  edit: result,
                })
              }
              onDelete={setDeleting}
            />
          ),
        };
      });
    items = [...legacy, ...results].sort((a, b) => b.at - a.at);
  }

  return (
    <div className="Records">
      <div className="Records__Title__Content">
        <p>{t('records.title')}</p>
        <Button
          size="sm"
          onClick={() => {
            getHistoryData();
            feed.reload();
          }}
        >
          {isLoading ? (
            <Loading color="primary" size="xs" />
          ) : (
            <>
              <ArrowPathIcon className="Records__Title__Icon" />
              {t('records.refreshButton')}
            </>
          )}
        </Button>
      </div>
      <hr className="Records__Line" />
      {isLoading ? (
        <Loading
          className="flex justify-center h-80 mx-auto"
          color="primary"
          size="lg"
        />
      ) : (
        <div className="Records__Container">
          {items.map((item) => item.node)}
          {feed.hasMore ? (
            <Button
              size="sm"
              color="ghost"
              loading={feed.loading}
              onClick={() => void feed.loadMore()}
            >
              {t('leadClose.history.more')}
            </Button>
          ) : null}
        </div>
      )}
      {deleting && selectedLeadForChatModal ? (
        <LeadCloseDeleteConfirm
          leadId={selectedLeadForChatModal.id}
          leadName={selectedLeadForChatModal.name}
          result={deleting}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
